import type { LoadScriptProps } from '@react-google-maps/api';
import { GoogleMap, MarkerF, useLoadScript } from '@react-google-maps/api';
import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { Loader } from '@/components/Loader';
import type { StoreHit } from '@/integrations/algolia/types';
import { GOOGLEMAPS_API_KEY } from '@/lib/const';
import { messages } from '@/messages/boutique';

import markerIcon from '@/assets/icons/marker.svg';

import * as S from './BoutiqueMap.styles';
type BoutiqueMapProps = {
  boutiqueHit: StoreHit;
  islocalized: boolean;
};
const libraries = ['places'];
const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

export const BoutiqueMap = ({
  boutiqueHit,
  islocalized = false,
}: BoutiqueMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const { formatMessage } = useIntl();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLEMAPS_API_KEY,
    libraries,
  } as LoadScriptProps);
  useEffect(() => {
    if (islocalized && mapRef.current) {
      const elementTop = mapRef.current.getBoundingClientRect().top;
      const scrollPosition = window.scrollY + elementTop - 160;
      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    }
  }, [islocalized, isLoaded]);

  if (loadError) {
    return <div>{formatMessage(messages.errorLoadingMap)}</div>;
  }

  if (!boutiqueHit._geoloc) return null;

  if (!isLoaded) {
    return <Loader height={400} />;
  }

  return (
    <div ref={mapRef} id='googleMap'>
      <S.MapContainer>
        {boutiqueHit?._geoloc && (
          <GoogleMap
            center={boutiqueHit?._geoloc}
            mapContainerStyle={mapContainerStyle}
            zoom={17}
          >
            <MarkerF icon={markerIcon} position={boutiqueHit?._geoloc} />
          </GoogleMap>
        )}
      </S.MapContainer>
    </div>
  );
};
